import type {
  HygienistInsights,
  TBenchmarkingCounts,
  TBenchmarkingProviderInsights,
  THygieneTaskType,
  TProviderRole,
  TTreatmentType,
} from '../../../react-query/insights/types';

/**
 * Types for the clinical dashboard
 *
 * These types are used to transform the data from the API response into
 * the shapes used to power graphs, tables and user interactions
 * across the clinical dashboard.
 */

// TBaseCountsByStatus is the first layer of transformation of the data from
// the API response. It is used to calculate the rates and values for each
// treatment type and status from BaseTreatmentData.
export type TBaseCountsByStatus = {
  recos: number;
  recosByTx: { [key in TTreatmentType]?: number };
};

// TBaseCounts holds the counts for each treatment type by status.
export type TBaseCounts = {
  total: TBaseCountsByStatus;
  unplanned: TBaseCountsByStatus;
  planned: TBaseCountsByStatus;
  scheduled: TBaseCountsByStatus;
  completed: TBaseCountsByStatus;
};

// TBaseRatesByStatus holds the rates and values for each treatment type by status.
export type TBaseRatesByStatus = {
  rate: number;
  rateByTx: { [key in TTreatmentType]?: number };
  rateChange: number;
  rateChangeByTx: { [key in TTreatmentType]?: number };

  value: number;
  valueByTx: { [key in TTreatmentType]?: number };
  valueChange: number;
  valueChangeByTx: { [key in TTreatmentType]?: number };

  opportunity: number;
  opportunityByTx: { [key in TTreatmentType]?: number };
};

// TBaseRates holds the rates and values for each treatment type by status and preserves
// the previous and current counts that were used to calculate the rates.
export type TBaseRates = {
  current: TBaseCounts;
  previous: TBaseCounts;

  /* @Deprecated for now as we only care about unplanned
  planned: TBaseRatesByStatus;
  scheduled: TBaseRatesByStatus;
  completed: TBaseRatesByStatus;
  */
};

// Provider production rates associated with
export type TProductionRates = {
  // production here represents the rate of current versus previous period per treatment type
  production: {
    [key in TTreatmentType]: TTreamentProduction;
  };
  // productionRatio here represents the ratio of production to opportunity (reco opp + production) for this provider
  productionRatio?: { [key in TTreatmentType]: number };

  // overallProductionValue here represents the sum of production for all treatment types
  overallProduction?: number;
  overallProductionChange?: number;

  // overallProductionRatio here represents the ratio of production to opportunity (reco opp + production) for this provider
  overallProductionRatio?: number;
  overallProductionRatioChange?: number;
};

export type TTreamentProduction = {
  current?: number;
  previous?: number;
  rateChange?: number;
};

// TInsightsRates holds the rates, values and opportunities broken down by production vs AI recommendations
export type TInsightsRates = {
  // recommendations represent AI recommendations for this provider
  recommendations: TBaseRates;

  // production represent completed treatments for this provider directly off of PMS + comparison to recos
  production: TProductionRates;
};

// TProviderRates holds the rates and values for each treatment type by status for a provider (and practice).
export type TProviderRates = TInsightsRates & {
  practiceId: number;
  practiceName: string;
  providerId: number;
  providerName: string;
  providerRole?: TProviderRole;
};

// TPracticeRates holds the rates and values for each treatment type by status for a practice as well summary.
export type TPracticeRates = TInsightsRates & {
  practiceId: number;
  practiceName: string;
  providers: TProviderRates[];
};

export type TRegionRates = TInsightsRates & {
  regionId: string;
  regionName: string;
  practices: TPracticeRates[];
};

// We need to extend the type from the API response to be able to calculate rates and opportunities
export type TProviderInsights = TBenchmarkingProviderInsights & {
  practiceId: number;
  practiceName: string;
  providerRole?: string;
  estimatedTreatmentValues: { [key in TTreatmentType]: { value: number } };
};

// TBucket creates logical groupings of TBaseRates (and derived types) based on the rate.
// The ids are used to filter the TBaseRates (and derived types) that fall into the bucket.
export type TBucket = {
  count: number;
  ids: number[];
};

// TBuckets holds the buckets for each treatment type by status.
export type TBuckets = {
  planned: TBucket[];
  scheduled: TBucket[];
  completed: TBucket[];
  filteredIds: number[] | undefined;
};

// TSummary rates is the fully composed type that is used to power the clinical dashboard.
// It holds the summary rates (TBaseRates), the rates for each practice or provider, and the buckets.
export type TSummaryRates = TBaseRates & {
  rates: TPracticeRates[] | TProviderRates[];
  buckets: TBuckets; // Buckets of rates currently filtered
  totalGroups: number; // Total number of groups in the summary
};

type TPeriodData = {
  total?: TBenchmarkingCounts;
  planned?: TBenchmarkingCounts;
  scheduled?: TBenchmarkingCounts;
  completed?: TBenchmarkingCounts;
  unplanned?: TBenchmarkingCounts;
};

export type TTreatmentData = {
  current?: TPeriodData;
  previous?: TPeriodData;
};

/**
 * Types for the hygiene dashboard
 *
 * These types are used to transform the data from the API response into
 * the shapes used to power graphs, tables and user interactions
 * across the hygiene dashboard.
 */

export type THygieneProviderInsights = HygienistInsights & {
  practiceId: string;
  practiceName: string;
  estimatedHygieneValues: { [key in THygieneTaskType]: { value: number } };
};

export type THygieneProviderRates = THygieneRates & {
  practiceId: string;
  practiceName: string;
  providerId: string;
  providerName: string;
  providerRole?: TProviderRole;
};

export type THygienePracticeRates = THygieneRates & {
  practiceId: string;
  practiceName: string;
  hygienists: THygieneProviderRates[];
};

export type THygieneRegionRates = THygieneRates & {
  regionId: string;
  regionName: string;
  practices: THygienePracticeRates[];
};

export type THygieneRates = {
  production: THygieneTasks;
  opportunity: THygieneTasks;

  // productionRatio here represents the ratio of production to opportunity (reco opp + production) for this provider
  productionRatio?: number;
  productionRatioChange?: number;

  estimatedHygieneValues: { [key in THygieneTaskType]: { value: number } };
};

export type THygieneTasks = {
  // Break down tasks by current and previous period plus the rate change for each task
  tasks: {
    [key in THygieneTaskType]: THygieneTaskPeriod;
  };
  overallTasks?: number;
  overallTasksChange?: number;
};

export type THygieneTaskPeriod = {
  current: number;
  previous: number;
  rateChange?: number;
};

// Mapping from task codes to THygieneTaskType
export const TASK_CODE_TO_TYPE: Record<string, THygieneTaskType> = {
  BW_DUE: 'Bitewings',
  PROPHY_DUE: 'Prophy',
  PERIO_CHART_DUE: 'Perio Chart',
  PERIO_MAINTENANCE_DUE: 'Perio Maintenance',
  // Add more mappings if needed
};

// Mapping from THygieneTaskType to task codes
export const TYPE_TO_TASK_CODE: Record<THygieneTaskType, string> = {
  Bitewings: 'BW_DUE',
  Prophy: 'PROPHY_DUE',
  'Perio Chart': 'PERIO_CHART_DUE',
  'Perio Maintenance': 'PERIO_MAINTENANCE_DUE',
};

export type TTotalOpp = {
  current?: {
    opportunity: number;
    completed: number;
  };
  previous?: {
    opportunity: number;
    completed: number;
  };
};
